import { motion } from "framer-motion";
import { Car, Users, Luggage, Snowflake, Leaf } from "lucide-react";
import { useLang } from "../../site-context";

const EASE = [0.22, 1, 0.36, 1];

export default function Fleet() {
  const { t } = useLang();
  const chips = [
    { icon: Car, label: t.fleet_chip_brand },
    { icon: Users, label: t.fleet_chip_van },
    { icon: Leaf, label: t.fleet_chip_electric },
    { icon: Luggage, label: t.fleet_chip_luggage },
    { icon: Snowflake, label: t.fleet_chip_ac },
  ];

  return (
    <section id="frota" className="relative bg-[#0d1830] py-24 lg:py-32" data-testid="fleet-section">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: EASE }}
          className="mb-14 max-w-2xl"
        >
          <p className="ff-mono mb-4 text-[11px] uppercase tracking-[0.35em] text-[#e3a008] sm:text-xs">
            {t.fleet_eyebrow}
          </p>
          <h2 className="ff-display text-3xl font-semibold tracking-tight text-white sm:text-4xl lg:text-5xl">
            {t.fleet_title}
          </h2>
          <p className="mt-4 text-base font-light leading-relaxed text-slate-400 sm:text-lg">{t.fleet_sub}</p>
        </motion.div>

        <div className="grid gap-6 lg:grid-cols-12">
          <motion.figure
            data-testid="fleet-card-mercedes-1"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.9, ease: EASE }}
            className="gold-frame group relative overflow-hidden rounded-2xl lg:col-span-7"
          >
            <img
              src="/assets/fleet-church.jpg"
              alt="As quatro viaturas Mercedes-Benz da frota"
              className="h-full min-h-[300px] w-full object-cover transition-transform duration-[1.2s] ease-out group-hover:scale-105"
              data-testid="fleet-image-modal-trigger"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0a1120]/85 via-transparent to-transparent" />
            <figcaption className="absolute bottom-0 left-0 p-6">
              <span className="ff-display block text-2xl font-semibold text-white">{t.fleet_main_title}</span>
              <span className="mt-1 block text-sm font-light text-slate-300">{t.fleet_main_desc}</span>
            </figcaption>
          </motion.figure>

          <motion.figure
            data-testid="fleet-card-mercedes-2"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.9, delay: 0.15, ease: EASE }}
            className="gold-frame group relative overflow-hidden rounded-2xl lg:col-span-5"
          >
            <img
              src="/assets/suv-manor.jpg"
              alt="Táxi Mercedes-Benz SUV em serviço"
              className="h-full min-h-[300px] w-full object-cover transition-transform duration-[1.2s] ease-out group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0a1120]/85 via-transparent to-transparent" />
            <figcaption className="absolute bottom-0 left-0 p-6">
              <span className="ff-display block text-2xl font-semibold text-white">{t.fleet_suv_title}</span>
              <span className="mt-1 block text-sm font-light text-slate-300">{t.fleet_suv_desc}</span>
            </figcaption>
          </motion.figure>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.2, ease: EASE }}
          className="mt-8 flex flex-wrap gap-3"
        >
          {chips.map((c) => (
            <span
              key={c.label}
              className="ff-mono flex items-center gap-2 rounded-full border border-[#e3a008]/25 bg-[#12233f]/70 px-4 py-2 text-xs uppercase tracking-wider text-slate-300"
            >
              <c.icon className="h-4 w-4 text-[#e3a008]" strokeWidth={1.75} />
              {c.label}
            </span>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
